import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
// Zod schemas for validation
export const createUserRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
});
export const createUserResponseSchema = z.object({
    // id: z.string(),
    email: z.string().email(),
    verificationToken: z.string(),
    // createdAt: z.coerce.date(),
});
export const verifyEmailRequestSchema = z.object({
    verificationToken: z.string().trim().min(1),
    code: z
        .string()
        .trim()
        .length(6)
        .regex(/^\d{6}$/),
});
export const resendEmailVerificationRequestSchema = z.object({
    email: z.string().trim().email(),
});
export const resendEmailVerificationResponseSchema = z.object({
    verificationToken: z.string(),
});
export const requestPasswordResetRequestSchema = z.object({
    email: z.string().trim().email(),
});
export const resetPasswordRequestSchema = z.object({
    token: z.string().trim().min(1),
    password: z.string().min(8),
});
export const userSchema = z.object({
    email: z.string().email(),
    isEmailVerified: z.boolean(),
    emailVerificationCodeExpiresAt: z.coerce.date().optional(),
    isTwoFactorEnabled: z.boolean(),
    isActive: z.boolean(),
    roles: z.array(z.string()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Alternative hook that accepts form data with confirmPassword and strips it
export const useSignup = (request) => useMutation({
    mutationFn: (formData) => {
        // Extract only the fields needed for the API
        const userData = {
            email: formData.email,
            password: formData.password,
        };
        // Validate the request data
        const validatedData = createUserRequestSchema.parse(userData);
        return request({
            url: '/users',
            method: 'POST',
            data: validatedData,
        }, createUserResponseSchema);
    },
});
// TanStack Query mutation hook for email verification
export const useVerifyEmail = (request) => useMutation({
    mutationFn: (data) => {
        // Validate the request data
        const validatedData = verifyEmailRequestSchema.parse(data);
        return request({
            url: '/users/verify-email',
            method: 'POST',
            data: validatedData,
        });
    },
});
/**
 * Requests a new verification code for an address still awaiting verification. Resolves
 * with a fresh verification token, which replaces the one held for the earlier code.
 */
export const useResendEmailVerification = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = resendEmailVerificationRequestSchema.parse(data);
        return request({
            url: '/users/resend-verification',
            method: 'POST',
            data: validatedData,
        }, resendEmailVerificationResponseSchema);
    },
});
/**
 * Asks for a password reset link. Resolves the same way whether or not the address has an
 * account - the server tells the inbox, not the caller - so the screen must not try to
 * report which happened.
 */
export const useRequestPasswordReset = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = requestPasswordResetRequestSchema.parse(data);
        return request({
            url: '/users/password-reset/request',
            method: 'POST',
            data: validatedData,
        });
    },
});
/** Sets a new password from a reset link. */
export const useResetPassword = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = resetPasswordRequestSchema.parse(data);
        return request({
            url: '/users/password-reset/confirm',
            method: 'POST',
            data: validatedData,
        });
    },
});
// TanStack Query hook for fetching current user
export const useGetCurrentUser = (authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['currentUser'],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: '/users/me', method: 'GET' }, userSchema);
            return response;
        },
        enabled: autoFetch,
    });
};
